// ============================================================
//  PremiumModal.jsx — Modale d'abonnement Premium MyCompanion AI
//  Coller dans le projet Emergent (composant React)
// ============================================================

import React, { useEffect, useState } from 'react';

// ── Traductions ──────────────────────────────────────────────
const TRANSLATIONS = {
  fr: {
    eyebrow: 'Abonnement Premium',
    title: 'Débloque tout',
    subtitle: 'Vis une relation complète et profonde avec ton compagnon.',
    price: '6,99 €',
    priceFull: '16,99 €',
    period: '/ mois',
    note: 'Annulable à tout moment · Sans engagement',
    features: [
      { icon: '✨', title: 'Tous les niveaux débloqués', body: 'Découverte, Connaissance, Amitié, Rapprochement, Romance — la relation complète sans paliers à débloquer.' },
      { icon: '📸', title: 'Toutes les photos débloquées', body: 'Galeries complètes de chaque personnage.' },
      { icon: '💬', title: '200 messages par personnage', body: 'Des conversations longues et profondes avec chacun de tes compagnons.' },
    ],
    subscribe: "S'abonner — 6,99 €/mois",
    close: 'Plus tard',
  },
  en: {
    eyebrow: 'Premium Subscription',
    title: 'Unlock everything',
    subtitle: 'Experience a complete and deep relationship with your companion.',
    price: '€6.99',
    priceFull: '€16.99',
    period: '/ month',
    note: 'Cancel anytime · No commitment',
    features: [
      { icon: '✨', title: 'All levels unlocked', body: 'Discovery, Acquaintance, Friendship, Closeness, Romance — the full relationship without paywalls.' },
      { icon: '📸', title: 'All photos unlocked', body: 'Complete galleries for each character.' },
      { icon: '💬', title: '200 messages per character', body: 'Long, deep conversations with each of your companions.' },
    ],
    subscribe: 'Subscribe — €6.99/month',
    close: 'Later',
  },
  es: {
    eyebrow: 'Suscripción Premium',
    title: 'Desbloquea todo',
    subtitle: 'Vive una relación completa y profunda con tu compañero.',
    price: '6,99 €',
    priceFull: '16,99 €',
    period: '/ mes',
    note: 'Cancelable en cualquier momento · Sin compromiso',
    features: [
      { icon: '✨', title: 'Todos los niveles desbloqueados', body: 'Descubrimiento, Conocimiento, Amistad, Acercamiento, Romance — la relación completa.' },
      { icon: '📸', title: 'Todas las fotos desbloqueadas', body: 'Galerías completas de cada personaje.' },
      { icon: '💬', title: '200 mensajes por personaje', body: 'Conversaciones largas y profundas con cada uno de tus compañeros.' },
    ],
    subscribe: 'Suscribirse — 6,99 €/mes',
    close: 'Después',
  },
  pt: {
    eyebrow: 'Assinatura Premium',
    title: 'Desbloqueie tudo',
    subtitle: 'Viva um relacionamento completo e profundo com seu companheiro.',
    price: '€ 6,99',
    priceFull: '€ 16,99',
    period: '/ mês',
    note: 'Cancelável a qualquer momento · Sem compromisso',
    features: [
      { icon: '✨', title: 'Todos os níveis desbloqueados', body: 'Descoberta, Conhecimento, Amizade, Aproximação, Romance — o relacionamento completo.' },
      { icon: '📸', title: 'Todas as fotos desbloqueadas', body: 'Galerias completas de cada personagem.' },
      { icon: '💬', title: '200 mensagens por personagem', body: 'Conversas longas e profundas com cada um dos seus companheiros.' },
    ],
    subscribe: 'Assinar — € 6,99/mês',
    close: 'Depois',
  },
  de: {
    eyebrow: 'Premium-Abonnement',
    title: 'Schalte alles frei',
    subtitle: 'Erlebe eine vollständige und tiefe Beziehung mit deinem Begleiter.',
    price: '6,99 €',
    priceFull: '16,99 €',
    period: '/ Monat',
    note: 'Jederzeit kündbar · Keine Verpflichtung',
    features: [
      { icon: '✨', title: 'Alle Ebenen freigeschaltet', body: 'Entdeckung, Bekanntschaft, Freundschaft, Annäherung, Romanze — die vollständige Beziehung.' },
      { icon: '📸', title: 'Alle Fotos freigeschaltet', body: 'Komplette Galerien jedes Charakters.' },
      { icon: '💬', title: '200 Nachrichten pro Charakter', body: 'Lange und tiefe Gespräche mit jedem deiner Begleiter.' },
    ],
    subscribe: 'Abonnieren — 6,99 €/Monat',
    close: 'Später',
  },
};

// ── Composant principal ──────────────────────────────────────
export default function PremiumModal({ isOpen, onClose, onSubscribe, lang = 'fr' }) {
  const t = TRANSLATIONS[lang] || TRANSLATIONS.fr;
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      const timer = setTimeout(() => setIsMounted(false), 300);
      return () => clearTimeout(timer);
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && isOpen) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isMounted) return null;

  return (
    <>
      <style>{`
        @keyframes mc-fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes mc-fade-out { from { opacity: 1; } to { opacity: 0; } }
        @keyframes mc-slide-up { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes mc-slide-down { from { transform: translateY(0); opacity: 1; } to { transform: translateY(20px); opacity: 0; } }
        .mc-overlay {
          position: fixed; inset: 0;
          background: rgba(20, 12, 6, 0.55);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          z-index: 9999;
          display: flex; align-items: center; justify-content: center;
          padding: 16px;
          animation: mc-fade-in 0.25s ease-out;
        }
        .mc-overlay.closing { animation: mc-fade-out 0.25s ease-out forwards; }
        .mc-modal {
          background: #FAF6F1;
          border-radius: 28px;
          max-width: 520px;
          width: 100%;
          max-height: 92vh;
          overflow-y: auto;
          box-shadow: 0 24px 80px rgba(61, 46, 34, 0.25);
          animation: mc-slide-up 0.3s cubic-bezier(0.32, 0.72, 0, 1);
          position: relative;
        }
        .mc-overlay.closing .mc-modal { animation: mc-slide-down 0.25s ease-in forwards; }
        .mc-close-btn {
          position: absolute; top: 16px; right: 16px;
          width: 36px; height: 36px;
          border-radius: 50%;
          background: rgba(61, 46, 34, 0.08);
          border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: #6B4A2F;
          font-size: 1rem;
          transition: background 0.2s;
          z-index: 2;
        }
        .mc-close-btn:hover { background: rgba(198, 125, 91, 0.18); }
        .mc-header {
          text-align: center;
          padding: 36px 28px 24px;
          background: linear-gradient(180deg, rgba(198, 125, 91, 0.08) 0%, transparent 100%);
        }
        .mc-eyebrow {
          display: inline-block;
          font-family: 'Jost', sans-serif;
          font-size: 0.72rem;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          font-weight: 600;
          color: #A65D3F;
          background: rgba(198, 125, 91, 0.12);
          padding: 6px 14px;
          border-radius: 100px;
          margin-bottom: 14px;
        }
        .mc-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.85rem;
          font-weight: 500;
          line-height: 1.15;
          color: #3D2E22;
          margin: 0 0 10px;
          letter-spacing: -0.01em;
        }
        .mc-title em { font-style: italic; color: #C67D5B; }
        .mc-subtitle {
          font-family: 'Jost', sans-serif;
          font-size: 0.95rem;
          color: #6B5A4D;
          line-height: 1.5;
          font-weight: 300;
          max-width: 380px;
          margin: 0 auto;
        }
        .mc-price-row {
          display: flex; align-items: baseline; justify-content: center;
          gap: 6px;
          margin-top: 18px;
        }
        .mc-price {
          font-family: 'Cormorant Garamond', serif;
          font-size: 2.6rem;
          font-weight: 600;
          color: #A65D3F;
          line-height: 1;
        }
        .mc-price-full {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.4rem;
          font-weight: 400;
          color: #8B7866;
          text-decoration: line-through;
          text-decoration-color: rgba(139, 120, 102, 0.7);
          line-height: 1;
        }
        .mc-period {
          font-family: 'Jost', sans-serif;
          font-size: 0.95rem;
          color: #6B5A4D;
          font-weight: 400;
        }
        .mc-note {
          font-family: 'Jost', sans-serif;
          font-size: 0.78rem;
          color: #8B7866;
          margin-top: 6px;
          font-weight: 400;
        }
        .mc-features {
          padding: 8px 28px 24px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .mc-feature {
          display: flex;
          gap: 14px;
          align-items: flex-start;
          padding: 14px 16px;
          background: #fff;
          border-radius: 14px;
          border: 1px solid rgba(61, 46, 34, 0.05);
        }
        .mc-feature-icon {
          flex-shrink: 0;
          width: 38px; height: 38px;
          border-radius: 10px;
          background: rgba(198, 125, 91, 0.1);
          display: flex; align-items: center; justify-content: center;
          font-size: 1.2rem;
        }
        .mc-feature-text { flex: 1; min-width: 0; }
        .mc-feature-title {
          font-family: 'Jost', sans-serif;
          font-size: 0.92rem;
          font-weight: 600;
          color: #3D2E22;
          margin: 0 0 3px;
          line-height: 1.3;
        }
        .mc-feature-body {
          font-family: 'Jost', sans-serif;
          font-size: 0.82rem;
          color: #6B5A4D;
          line-height: 1.45;
          margin: 0;
          font-weight: 300;
        }
        .mc-actions {
          padding: 0 28px 32px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .mc-btn-primary {
          width: 100%;
          padding: 16px 22px;
          border-radius: 100px;
          background: #C67D5B;
          color: #fff;
          font-family: 'Jost', sans-serif;
          font-size: 1rem;
          font-weight: 600;
          border: none; cursor: pointer;
          box-shadow: 0 6px 20px rgba(198, 125, 91, 0.35);
          transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
          letter-spacing: 0.01em;
        }
        .mc-btn-primary:hover {
          background: #A65D3F;
          box-shadow: 0 8px 28px rgba(198, 125, 91, 0.45);
        }
        .mc-btn-primary:active { transform: scale(0.98); }
        .mc-btn-secondary {
          width: 100%;
          padding: 12px 22px;
          background: transparent;
          color: #6B5A4D;
          font-family: 'Jost', sans-serif;
          font-size: 0.88rem;
          font-weight: 400;
          border: none; cursor: pointer;
          transition: color 0.2s;
        }
        .mc-btn-secondary:hover { color: #3D2E22; }
        @media (max-width: 480px) {
          .mc-title { font-size: 1.6rem; }
          .mc-price { font-size: 2.2rem; }
          .mc-header { padding: 32px 22px 20px; }
          .mc-features { padding: 8px 22px 20px; }
          .mc-actions { padding: 0 22px 28px; }
        }
      `}</style>

      <div
        className={`mc-overlay ${!isOpen ? 'closing' : ''}`}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mc-modal-title"
        data-testid="premium-modal"
      >
        <div className="mc-modal">
          <button
            className="mc-close-btn"
            onClick={onClose}
            aria-label={t.close}
          >
            ✕
          </button>

          <div className="mc-header">
            <span className="mc-eyebrow">{t.eyebrow}</span>
            <h2 className="mc-title" id="mc-modal-title">
              {t.title}
            </h2>
            <p className="mc-subtitle">{t.subtitle}</p>
            <div className="mc-price-row">
              {t.priceFull && <span className="mc-price-full">{t.priceFull}</span>}
              <span className="mc-price">{t.price}</span>
              <span className="mc-period">{t.period}</span>
            </div>
            <p className="mc-note">{t.note}</p>
          </div>

          <div className="mc-features">
            {t.features.map((f, i) => (
              <div className="mc-feature" key={i}>
                <div className="mc-feature-icon" aria-hidden="true">{f.icon}</div>
                <div className="mc-feature-text">
                  <p className="mc-feature-title">{f.title}</p>
                  <p className="mc-feature-body">{f.body}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mc-actions">
            <button className="mc-btn-primary" onClick={onSubscribe}>
              {t.subscribe}
            </button>
            <button className="mc-btn-secondary" onClick={onClose}>
              {t.close}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ============================================================
//  EXEMPLE D'UTILISATION dans ton app
// ============================================================
//
// import PremiumModal from './PremiumModal';
//
// function MyComponent() {
//   const [showPremium, setShowPremium] = useState(false);
//
//   const handleSubscribe = () => {
//     // Détecter la plateforme
//     const isAndroidApp = window.AndroidBridge !== undefined; // ou ton détecteur
//
//     if (isAndroidApp) {
//       // Appeler Google Play Billing
//       window.AndroidBridge.launchBilling('premium_monthly');
//     } else {
//       // Web/PWA → Stripe (à configurer plus tard)
//       // window.location.href = '/api/create-checkout-session';
//       alert('Stripe à configurer côté serveur');
//     }
//   };
//
//   return (
//     <>
//       <button onClick={() => setShowPremium(true)}>
//         Abonnement Premium
//       </button>
//
//       <PremiumModal
//         isOpen={showPremium}
//         onClose={() => setShowPremium(false)}
//         onSubscribe={handleSubscribe}
//         lang="fr"  // ou détecté dynamiquement
//       />
//     </>
//   );
// }
